import { Editor, mergeAttributes, Node } from "@tiptap/core"
import { VueNodeViewRenderer } from "@tiptap/vue-3"

import EditorAttachment from "@/components/Editor/EditorAttachment.vue"

import { dropAttachmentPlugin, FileType, TypeConfig, UploadAttachmentFn, VUFile } from "@/models"
import { ATTACHMENT_IMG_HEIGHT, ATTACHMENT_IMG_WIDTH } from "@/utils/Constans"
import {
  filePreviewForEditor,
  insertAttachmentPreview,
  InsertFilePreview,
  uploadAttachments
} from "@/utils/helpers"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    file: {
      setFile: (files: VUFile[], config: TypeConfig | null) => ReturnType
      removeAttachmentById: (id: string, fileType: FileType) => ReturnType
    }
  }
}

export const Attachment = (
  uploadFn: UploadAttachmentFn,
  config: TypeConfig | null,
  isResizable: boolean
) =>
  createAttachment(uploadFn, config, isResizable, editor => payload => {
    filePreviewForEditor({
      ...payload,
      editor
    })
  })

export const AttachmentFile = (
  uploadFn: UploadAttachmentFn,
  config: TypeConfig | null,
  isResizable: boolean
) => createAttachment(uploadFn, config, isResizable, () => insertAttachmentPreview)

const createAttachment = (
  uploadFn: UploadAttachmentFn,
  config: TypeConfig | null,
  isResizable: boolean,
  getInsertFilePreview: (editor: Editor) => InsertFilePreview
) => {
  return Node.create({
    name: "attachment",
    inline: true,
    group: "inline",
    draggable: true,

    addAttributes() {
      return {
        src: {},
        href: {},
        id: {},
        alt: { default: null },
        info: { default: "{}" },
        title: { default: null },
        isResizable: { default: true },
        type: { default: "img" },
        width: {
          default: ATTACHMENT_IMG_WIDTH
        },
        height: {
          default: ATTACHMENT_IMG_HEIGHT
        },
        customResize: {
          default: false
        },
        isSkeletor: {
          default: false
        }
      }
    },

    parseHTML() {
      return [{ tag: 'vue-component[data-type="draggable-item"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      const data = window.btoa(encodeURI(JSON.stringify(HTMLAttributes)))

      return [
        "vue-component",
        mergeAttributes(HTMLAttributes, { "data-type": "draggable-item", data, isResizable })
      ]
    },

    addNodeView() {
      return VueNodeViewRenderer(EditorAttachment)
    },

    // @ts-ignore
    addCommands() {
      const insertFilePreview = getInsertFilePreview(this.editor)

      return {
        setFile:
          (files, config) =>
          ({ view, tr }) => {
            tr.setMeta("preventDispatch", true)
            void uploadAttachments({
              upload: uploadFn,
              files,
              view,
              config,
              insertFilePreview
            })
            return true
          },
        removeAttachmentById:
          (id: string, fileType: FileType) =>
          ({ tr, state, dispatch }) => {
            let found = false

            state.doc.descendants((node, pos) => {
              const isAttachmentType =
                node.type.name === "attachment" ||
                node.type.name === "referenceHashFile" ||
                node.type.name === "referenceHashFileLink" ||
                node.type.name === "referenceAt"

              if (isAttachmentType && (node.attrs.id === id || node.attrs.info.includes(id))) {
                const nodeToReplace = state.schema.nodes.removedAttachment.create({
                  fileType,
                  id
                })

                tr.replaceWith(pos, pos + node.nodeSize, nodeToReplace)
                found = true
                return false
              }

              return true
            })

            if (found && dispatch) {
              dispatch(tr)
            }

            return found
          }
      }
    },

    addProseMirrorPlugins() {
      return [dropAttachmentPlugin(uploadFn, config, getInsertFilePreview(this.editor))]
    }
  })
}
